/**
 * FibrationDiagram — SVG rendering of π_I : 𝓔_I → 𝓥.
 *
 * Draws the base category 𝓥 (versions and raw changes) and the total
 * space 𝓔_I (evidence states and witness lifts) with the fibration
 * projection π_I between them. The selected mode chooses which step is
 * governed and which is a refusal, animating the 2-cell that fails to
 * lift.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { panelTokens } from "@csk/demo-ui";

export type FibrationMode =
  | "governed"
  | "ungoverned-admission"
  | "ungoverned-authority"
  | "ungoverned-meta";

export interface FibrationDiagramProps {
  readonly mode: FibrationMode;
  readonly width?: number;
  readonly height?: number;
}

const VERSION_LABELS = ["v₀", "v₁", "v₂"];

function liftColor(mode: FibrationMode): string {
  if (mode === "governed") return panelTokens.accentGreen;
  if (mode === "ungoverned-admission") return panelTokens.accentRed;
  if (mode === "ungoverned-authority") return panelTokens.accentPurple;
  return panelTokens.accentBlue;
}

function refusalLabel(mode: FibrationMode): string | null {
  if (mode === "governed") return null;
  if (mode === "ungoverned-admission")
    return "Sᴬ — admission widened (no bridge)";
  if (mode === "ungoverned-authority")
    return "Sᵁ — issuer out of scope (no ICAPExpansion)";
  return "Sᴹ — interpreter drift (no RegimeMigrationArtifact)";
}

export function FibrationDiagram({
  mode,
  width = 720,
  height = 360,
}: FibrationDiagramProps): React.JSX.Element {
  const padX = 60;
  const totalY = 100;
  const baseY = 270;
  const colWidth = (width - 2 * padX) / 2;
  const lift = liftColor(mode);
  const refusal = refusalLabel(mode);

  const totalNodeY = totalY;
  const baseNodeY = baseY;
  const r = 28;

  const totalXs = [padX, padX + colWidth, padX + 2 * colWidth];
  const baseXs = totalXs;

  const isGoverned = mode === "governed";

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      className="block"
      style={{ maxWidth: width }}
      role="img"
      aria-label="Fibration π_I from total space 𝓔_I down to base 𝓥"
    >
      <defs>
        <marker
          id="ilg-arrow"
          viewBox="0 -5 10 10"
          refX="10"
          refY="0"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M0,-5L10,0L0,5" fill={panelTokens.muted} />
        </marker>
        <marker
          id="ilg-arrow-accent"
          viewBox="0 -5 10 10"
          refX="10"
          refY="0"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M0,-5L10,0L0,5" fill={lift} />
        </marker>
        <marker
          id="ilg-arrow-red"
          viewBox="0 -5 10 10"
          refX="10"
          refY="0"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M0,-5L10,0L0,5" fill={panelTokens.accentRed} />
        </marker>
      </defs>

      <text
        x={20}
        y={totalNodeY - 50}
        fill={panelTokens.muted}
        fontSize={11}
        fontFamily={panelTokens.mono}
        letterSpacing="0.12em"
      >
        𝓔_I — evidence states + witness lifts
      </text>

      <text
        x={20}
        y={baseNodeY + 70}
        fill={panelTokens.muted}
        fontSize={11}
        fontFamily={panelTokens.mono}
        letterSpacing="0.12em"
      >
        𝓥 — versions + raw changes
      </text>

      {totalXs.map((x, i) => (
        <g key={`total-${i}`}>
          <circle
            cx={x}
            cy={totalNodeY}
            r={r}
            fill={panelTokens.elev}
            stroke={panelTokens.border}
            strokeWidth={1.5}
          />
          <text
            x={x}
            y={totalNodeY + 4}
            textAnchor="middle"
            fill={panelTokens.bright}
            fontSize={13}
            fontFamily={panelTokens.mono}
            fontWeight={600}
          >
            E{i}
          </text>
        </g>
      ))}

      {baseXs.map((x, i) => (
        <g key={`base-${i}`}>
          <circle
            cx={x}
            cy={baseNodeY}
            r={r}
            fill={panelTokens.elev}
            stroke={panelTokens.border}
            strokeWidth={1.5}
          />
          <text
            x={x}
            y={baseNodeY + 4}
            textAnchor="middle"
            fill={panelTokens.bright}
            fontSize={13}
            fontFamily={panelTokens.mono}
            fontWeight={600}
          >
            {VERSION_LABELS[i]}
          </text>
        </g>
      ))}

      {/* π_I projections */}
      {totalXs.map((x, i) => (
        <g key={`pi-${i}`}>
          <line
            x1={x}
            y1={totalNodeY + r}
            x2={x}
            y2={baseNodeY - r}
            stroke={panelTokens.muted}
            strokeWidth={1}
            strokeDasharray="3 4"
            markerEnd="url(#ilg-arrow)"
          />
          {i === 1 && (
            <text
              x={x + 10}
              y={(totalNodeY + baseNodeY) / 2}
              fill={panelTokens.muted}
              fontSize={11}
              fontFamily={panelTokens.mono}
              fontStyle="italic"
            >
              π_I
            </text>
          )}
        </g>
      ))}

      {/* base arrows (raw changes) */}
      {[0, 1].map((i) => (
        <g key={`base-arrow-${i}`}>
          <line
            x1={baseXs[i] + r}
            y1={baseNodeY}
            x2={baseXs[i + 1] - r}
            y2={baseNodeY}
            stroke={panelTokens.muted}
            strokeWidth={1.5}
            markerEnd="url(#ilg-arrow)"
          />
          <text
            x={(baseXs[i] + baseXs[i + 1]) / 2}
            y={baseNodeY + 24}
            textAnchor="middle"
            fill={panelTokens.muted}
            fontSize={11}
            fontFamily={panelTokens.mono}
          >
            change c{i + 1}
          </text>
        </g>
      ))}

      {/* total arrows (witness lifts) */}
      {[0, 1].map((i) => {
        const fail = !isGoverned && i === 1;
        const color = fail ? panelTokens.accentRed : lift;
        const markerId = fail ? "ilg-arrow-red" : "ilg-arrow-accent";
        const dasharray = fail ? "5 5" : undefined;
        return (
          <g key={`total-arrow-${i}`}>
            <line
              x1={totalXs[i] + r}
              y1={totalNodeY}
              x2={totalXs[i + 1] - r}
              y2={totalNodeY}
              stroke={color}
              strokeWidth={2}
              markerEnd={`url(#${markerId})`}
              strokeDasharray={dasharray}
              style={{ transition: "stroke 200ms ease" }}
            />
            <text
              x={(totalXs[i] + totalXs[i + 1]) / 2}
              y={totalNodeY - 12}
              textAnchor="middle"
              fill={color}
              fontSize={11}
              fontFamily={panelTokens.mono}
              fontWeight={600}
            >
              {fail ? "✗ no lift" : `χ${i + 1}`}
            </text>
          </g>
        );
      })}

      {/* refusal call-out */}
      {refusal && (
        <g>
          <rect
            x={padX + colWidth + 20}
            y={totalNodeY - 70}
            width={width - padX - colWidth - 40}
            height={40}
            rx={8}
            fill="rgba(239,68,68,0.10)"
            stroke={panelTokens.accentRed}
            strokeOpacity={0.5}
          />
          <text
            x={padX + colWidth + 28}
            y={totalNodeY - 50}
            fill={panelTokens.accentRed}
            fontSize={11}
            fontFamily={panelTokens.mono}
            fontWeight={600}
          >
            seam: {refusal}
          </text>
          <text
            x={padX + colWidth + 28}
            y={totalNodeY - 35}
            fill={panelTokens.muted}
            fontSize={10}
            fontFamily={panelTokens.mono}
          >
            π_I⁻¹(c2) is empty — no governed E1 → E2
          </text>
        </g>
      )}

      {isGoverned && (
        <g>
          <rect
            x={padX + colWidth + 20}
            y={totalNodeY - 70}
            width={width - padX - colWidth - 40}
            height={40}
            rx={8}
            fill="rgba(34,197,94,0.10)"
            stroke={panelTokens.accentGreen}
            strokeOpacity={0.5}
          />
          <text
            x={padX + colWidth + 28}
            y={totalNodeY - 50}
            fill={panelTokens.accentGreen}
            fontSize={11}
            fontFamily={panelTokens.mono}
            fontWeight={600}
          >
            governed: every raw change c admits a witness lift χ
          </text>
          <text
            x={padX + colWidth + 28}
            y={totalNodeY - 35}
            fill={panelTokens.muted}
            fontSize={10}
            fontFamily={panelTokens.mono}
          >
            χ₂ ∘ χ₁ ∈ Mor(𝓔_I) — temporal integrity holds
          </text>
        </g>
      )}
    </svg>
  );
}
